import React from 'react';
import { STATUS_LABELS } from '../ds/StatusMap';
import { Box } from '@mui/material';
import { Icon, IconName } from '../ds/components/Icon';

/**
 * CeebBotReport — czytelna sekcja WYNIKU przebiegu bota CEEB (issue #51 / B7).
 *
 * Renderuje raport z `meta` zapisanej przez bota (GET /api/ceeb-demo/last-meta.json):
 *   • PUSTE_W_CEEB — pola puste w CEEB, WYRÓŻNIONE (wymagają ręcznego uzupełnienia),
 *   • carry-over — pola pominięte (bot ich nie ruszał; pokazujemy aktualną wartość z CEEB),
 *   • failedFields — pola, których nie udało się wypełnić,
 *   • lokaleDelta + apartments — które lokale utworzono / pominięto / z błędem,
 *   • retries — kroki odzyskane po ponownej próbie.
 *
 * Komponent PREZENTACYJNY (bez fetch/pollingu) — testowalny w izolacji (RTL).
 * KONTRAKT-API: kształt `meta` ustalony po stronie backendu (fillCeebDraft.js) —
 * czytamy go ADDYTYWNIE, tu nie zmieniamy. Kolory WYŁĄCZNIE z tokenów DS.
 */

// ---- Kształt raportowej części meta (wszystko opcjonalne — czytamy defensywnie) ----

export interface CeebSkippedField {
  field: string;
  /** Aktualna wartość w CEEB LUB sentinel 'PUSTE_W_CEEB' / 'POLE_NIEOBECNE'. */
  ceebValue: string;
}

export interface CeebFailedField {
  field: string;
  error: string;
}

export interface CeebRetryEntry {
  label: string;
  recoveredOnAttempt: number;
}

/** Wynik JEDNEGO źródła ciepła w raporcie bota (meta.apartments[].heatSources[]). */
export interface CeebHeatSourceResult {
  type?: string;
  /** Pominięte (np. „już w CEEB") — nie liczy się ani do prób, ani do sukcesów. */
  skipped?: string;
  /** Źródło w ogóle nie weszło (formularz nie wstał, typ nieznany). */
  error?: string;
  /** Pola źródła: `ok:false` = wartość NIE weszła do CEEB. */
  filled?: Array<{ field: string; value?: string; ok?: boolean; error?: string; skipped?: string }>;
}

export interface CeebApartmentResult {
  apartment_number: string;
  // utworzony:
  fields?: Array<{ field: string; value?: string; ok?: boolean; error?: string }>;
  heatSources?: CeebHeatSourceResult[];
  created?: boolean;
  /** Bot zaznaczył „wspólne źródło" z reguły domyślnej (Bronek 2026-10-14) — zapis bez informacji o źródłach. */
  defaultConnected?: boolean;
  // pominięty (status lokalu lub powód tekstowy):
  skipped?: string;
  // błąd:
  error?: string;
}

export interface CeebLokaleDelta {
  existing: Array<string | number>;
  toCreate: string[];
}

export interface CeebBotMeta {
  saved?: boolean | null;
  dryRun?: boolean;
  /** Zadanie 3.4: błędy CAŁYCH kroków (źródła budynku, tab 1/3, reopen) — dotąd nierenderowane. */
  errors?: Array<{ step: string; error: string }>;
  fields?: Array<{ field: string; value?: string; ok?: boolean; error?: string }>;
  skipped?: CeebSkippedField[];
  failedFields?: CeebFailedField[];
  retries?: CeebRetryEntry[];
  apartments?: CeebApartmentResult[];
  lokaleDelta?: CeebLokaleDelta;
}

const PUSTE = 'PUSTE_W_CEEB';
const NIEOBECNE = 'POLE_NIEOBECNE';

// Etykiety statusów lokali — z JEDNEJ współdzielonej mapy (StatusMap.ts, lustro backendu).
// Wcześniej ten komponent trzymał własną, czwartą już kopię, która zdążyła się rozjechać
// (miała `brak_uwag: 'Wykonano'` zamiast „Brak uwag").
// ⚠️ `reason` bywa też nie-statusem (powód pominięcia lokalu przez bota), więc zostaje
// przelotka `?? reason` — `statusLabel()` spłaszczyłoby takie napisy do „Nie zaplanowano".
const humanReason = (reason: string): string => STATUS_LABELS[reason] ?? reason;

type Tone = 'warn' | 'danger' | 'ok' | 'neutral' | 'info';

const TONE: Record<Tone, { bg: string; bar: string; fg: string; icon: IconName }> = {
  warn: { bg: 'var(--status-rec-bg)', bar: 'var(--status-rec-bar)', fg: 'var(--status-rec)', icon: 'triangle-alert' },
  danger: { bg: 'var(--status-deny-bg)', bar: 'var(--status-deny-bar)', fg: 'var(--status-deny)', icon: 'ban' },
  ok: { bg: 'var(--status-ok-bg)', bar: 'var(--status-ok-bar)', fg: 'var(--status-ok)', icon: 'check-circle' },
  neutral: { bg: 'var(--surface-fill)', bar: 'var(--status-todo-bar)', fg: 'var(--text-secondary)', icon: 'refresh' },
  info: { bg: 'var(--changed-bg)', bar: 'var(--changed-bar)', fg: 'var(--changed-text)', icon: 'refresh' },
};

// --- Sekcja raportu: kolorowy pasek + tint tła + ikona + tytuł + wiersze ---
const Section: React.FC<{
  tone: Tone;
  icon?: IconName;
  title: string;
  count?: number;
  children: React.ReactNode;
  testId?: string;
}> = ({ tone, icon, title, count, children, testId }) => {
  const t = TONE[tone];
  return (
    <Box
      data-testid={testId}
      sx={{
        background: t.bg,
        borderLeft: `var(--bar-width) solid ${t.bar}`,
        borderRadius: 'var(--radius-md)',
        p: 'var(--space-4)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: t.fg, mb: 'var(--space-2)' }}>
        <Box sx={{ flex: 'none', display: 'inline-flex' }}>
          <Icon name={icon ?? t.icon} size={20} />
        </Box>
        <Box sx={{ fontSize: 'var(--text-label)', fontWeight: 'var(--weight-semibold)', overflowWrap: 'anywhere' }}>
          {title}
          {typeof count === 'number' && (
            <Box component="span" className="num-tabular" sx={{ ml: 'var(--space-2)', fontWeight: 'var(--weight-regular)', color: 'var(--text-secondary)' }}>
              ({count})
            </Box>
          )}
        </Box>
      </Box>
      {children}
    </Box>
  );
};

// --- Wiersz „pole → wartość" ---
const Row: React.FC<{ label: React.ReactNode; value?: React.ReactNode; valueColor?: string }> = ({
  label,
  value,
  valueColor,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 'var(--space-1) var(--space-3)',
      py: 'var(--space-1)',
      fontSize: 'var(--text-caption)',
      lineHeight: 'var(--leading-snug)',
    }}
  >
    <Box sx={{ color: 'var(--text-primary)', fontWeight: 'var(--weight-medium)', overflowWrap: 'anywhere' }}>{label}</Box>
    {value != null && value !== '' && (
      <Box sx={{ color: valueColor ?? 'var(--text-secondary)', overflowWrap: 'anywhere' }}>{value}</Box>
    )}
  </Box>
);

const Chip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="span"
    className="num-tabular"
    sx={{
      display: 'inline-block',
      px: 'var(--space-2)',
      py: '2px',
      m: '2px',
      borderRadius: 'var(--radius-pill)',
      border: 'var(--border-width) solid var(--border-strong)',
      background: 'var(--surface-panel)',
      color: 'var(--text-primary)',
      fontSize: 'var(--text-caption)',
      fontWeight: 'var(--weight-medium)',
    }}
  >
    {children}
  </Box>
);

export interface CeebBotReportProps {
  meta: CeebBotMeta;
}

const CeebBotReport: React.FC<CeebBotReportProps> = ({ meta }) => {
  const skipped = meta.skipped ?? [];
  const failedFields = meta.failedFields ?? [];
  const retries = meta.retries ?? [];
  const apartments = meta.apartments ?? [];
  const lokaleDelta = meta.lokaleDelta;

  // Rozbij carry-over: puste w CEEB (wymaga ręcznego uzupełnienia) vs realna wartość vs pole nieobecne.
  const empty = skipped.filter((s) => s.ceebValue === PUSTE);
  const missing = skipped.filter((s) => s.ceebValue === NIEOBECNE);
  const carriedOver = skipped.filter((s) => s.ceebValue !== PUSTE && s.ceebValue !== NIEOBECNE);

  // Lokale: podział na utworzone / pominięte / z błędem.
  const aptCreated = apartments.filter((a) => Array.isArray(a.fields));
  const aptSkipped = apartments.filter((a) => !Array.isArray(a.fields) && a.skipped != null);
  const aptError = apartments.filter((a) => !Array.isArray(a.fields) && a.error != null);

  const okFields = (meta.fields ?? []).filter((f) => f.ok).length;
  const totalFields = (meta.fields ?? []).length;
  const stepErrors = meta.errors ?? [];

  const savedTone: Tone = meta.saved === true ? 'ok' : 'warn';
  const savedLabel =
    meta.saved === true
      ? 'Zapisano jako wersję roboczą w CEEB'
      : meta.dryRun
      ? 'Tryb próbny — nic nie zapisano (bez „Zapisz wersję roboczą")'
      : 'Nie zapisano — sprawdź błędy poniżej';

  return (
    <Box
      data-testid="ceeb-report"
      role="region"
      aria-label="Wynik przebiegu bota CEEB"
      sx={{
        mt: 'var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <Box
        sx={{
          fontSize: 'var(--text-h2)',
          fontWeight: 'var(--weight-bold)',
          color: 'var(--text-heading)',
        }}
      >
        Wynik przebiegu bota
      </Box>

      {/* Podsumowanie: status zapisu + liczby */}
      <Section tone={savedTone} icon={meta.saved === true ? 'check-circle' : 'triangle-alert'} title={savedLabel}>
        <Row
          label="Pola budynku uzupełnione"
          value={
            <Box component="span" className="num-tabular">
              {okFields}
              {totalFields ? ` / ${totalFields}` : ''}
            </Box>
          }
        />
        {apartments.length > 0 && (
          <Row
            label="Lokale utworzone"
            value={
              <Box component="span" className="num-tabular">
                {aptCreated.length} / {apartments.length}
              </Box>
            }
          />
        )}
      </Section>

      {/* PUSTE_W_CEEB — WYRÓŻNIONE: wymaga ręcznego uzupełnienia */}
      {empty.length > 0 && (
        <Section
          tone="warn"
          icon="triangle-alert"
          title="Puste w CEEB — wymaga ręcznego uzupełnienia"
          count={empty.length}
          testId="ceeb-report-puste"
        >
          <Box sx={{ mb: 'var(--space-2)', fontSize: 'var(--text-caption)', color: 'var(--status-rec)', fontWeight: 'var(--weight-medium)' }}>
            Bot nie nadpisuje tych pól, a w CEEB są puste — uzupełnij ręcznie w wersji roboczej.
          </Box>
          <Box>
            {empty.map((s) => (
              <Chip key={s.field}>{s.field}</Chip>
            ))}
          </Box>
        </Section>
      )}

      {/* Pola nieobecne w formularzu CEEB (inny wariant / lag renderu) */}
      {missing.length > 0 && (
        <Section
          tone="warn"
          icon="triangle-alert"
          title="Pola nieodnalezione w formularzu CEEB — sprawdź ręcznie"
          count={missing.length}
          testId="ceeb-report-missing"
        >
          <Box>
            {missing.map((s) => (
              <Chip key={s.field}>{s.field}</Chip>
            ))}
          </Box>
        </Section>
      )}

      {/* Zadanie 3.4: błędy CAŁYCH kroków przebiegu (dotąd tylko w logu bota) */}
      {stepErrors.length > 0 && (
        <Section
          tone="danger"
          icon="ban"
          title="Kroki zakończone błędem"
          count={stepErrors.length}
          testId="ceeb-report-step-errors"
        >
          {stepErrors.map((e, i) => (
            <Row key={`${e.step}-${i}`} label={e.step} value={e.error} valueColor="var(--status-deny)" />
          ))}
        </Section>
      )}

      {/* Nie udało się wypełnić (failedFields) */}
      {failedFields.length > 0 && (
        <Section
          tone="danger"
          icon="ban"
          title="Nie udało się wypełnić"
          count={failedFields.length}
          testId="ceeb-report-failed"
        >
          {failedFields.map((f, i) => (
            <Row key={`${f.field}-${i}`} label={f.field} value={f.error} valueColor="var(--status-deny)" />
          ))}
        </Section>
      )}

      {/* Pominięto jako carry-over — wartość już w CEEB */}
      {carriedOver.length > 0 && (
        <Section
          tone="neutral"
          icon="refresh"
          title="Pominięto — wartość już w CEEB (carry-over)"
          count={carriedOver.length}
          testId="ceeb-report-carryover"
        >
          {carriedOver.map((s) => (
            <Row key={s.field} label={s.field} value={<>w CEEB: <strong>{s.ceebValue}</strong></>} />
          ))}
        </Section>
      )}

      {/* Lokale: delta (istniejące / do utworzenia) + wynik per-lokal */}
      {(lokaleDelta || apartments.length > 0) && (
        <Section
          tone="info"
          icon="home"
          title="Lokale"
          testId="ceeb-report-lokale"
        >
          {lokaleDelta && (
            <>
              <Row
                label="Już w protokole CEEB"
                value={
                  lokaleDelta.existing.length ? (
                    <Box component="span">{lokaleDelta.existing.map((n) => String(n)).join(', ')}</Box>
                  ) : (
                    '(brak)'
                  )
                }
              />
              <Row
                label="Do utworzenia (z inwentaryzacją)"
                value={lokaleDelta.toCreate.length ? lokaleDelta.toCreate.join(', ') : '(brak)'}
              />
            </>
          )}

          {aptCreated.length > 0 && (
            <Box sx={{ mt: 'var(--space-2)' }}>
              <Box sx={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--weight-semibold)', color: 'var(--status-ok)', mb: 'var(--space-1)' }}>
                Utworzone lokale
              </Box>
              {aptCreated.map((a) => {
                const okCount = (a.fields ?? []).filter((f) => f.ok).length;
                // Zadanie 3.3 (2026-08-19): licznik liczy SUKCESY, nie próby. Dotąd „5 źr. ciepła"
                // pokazywało się także wtedy, gdy 4 z nich w ogóle nie weszły do CEEB (audyt B6).
                const hsAttempted = (a.heatSources ?? []).filter((h) => h && !h.skipped);
                const hsOk = hsAttempted.filter((h) => !h.error && !(h.filled ?? []).some((f) => f.ok === false));
                const hsPartial = hsOk.length < hsAttempted.length;
                return (
                  <Row
                    key={`c-${a.apartment_number}`}
                    label={<>Lokal <strong>{a.apartment_number}</strong></>}
                    value={
                      <Box component="span" className="num-tabular">
                        {okCount} pól, {hsOk.length}/{hsAttempted.length} źr. ciepła
                        {hsPartial ? ' — część źródeł NIE weszła' : ''}
                        {a.created === false ? ' — zapis niepewny' : ''}
                        {a.defaultConnected ? ' — domyślnie przyłączony do wspólnego (brak innego źródła w zapisie)' : ''}
                      </Box>
                    }
                    valueColor={hsPartial || a.created === false ? 'var(--status-rec)' : 'var(--status-ok)'}
                  />
                );
              })}
            </Box>
          )}

          {aptSkipped.length > 0 && (
            <Box sx={{ mt: 'var(--space-2)' }}>
              <Box sx={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--weight-semibold)', color: 'var(--text-secondary)', mb: 'var(--space-1)' }}>
                Pominięte lokale
              </Box>
              {aptSkipped.map((a) => (
                <Row
                  key={`s-${a.apartment_number}`}
                  label={<>Lokal <strong>{a.apartment_number}</strong></>}
                  value={humanReason(String(a.skipped))}
                />
              ))}
            </Box>
          )}

          {aptError.length > 0 && (
            <Box sx={{ mt: 'var(--space-2)' }}>
              <Box sx={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--weight-semibold)', color: 'var(--status-deny)', mb: 'var(--space-1)' }}>
                Lokale z błędem
              </Box>
              {aptError.map((a) => (
                <Row
                  key={`e-${a.apartment_number}`}
                  label={<>Lokal <strong>{a.apartment_number}</strong></>}
                  value={a.error}
                  valueColor="var(--status-deny)"
                />
              ))}
            </Box>
          )}
        </Section>
      )}

      {/* Odzyskane po ponownej próbie (retries) */}
      {retries.length > 0 && (
        <Section
          tone="info"
          icon="refresh"
          title="Odzyskane po ponownej próbie"
          count={retries.length}
          testId="ceeb-report-retries"
        >
          {retries.map((r, i) => (
            <Row
              key={`${r.label}-${i}`}
              label={r.label}
              value={
                <Box component="span" className="num-tabular">
                  udane w próbie {r.recoveredOnAttempt}
                </Box>
              }
            />
          ))}
        </Section>
      )}
    </Box>
  );
};

export default CeebBotReport;
